import React, { useState, useCallback, useEffect, useRef } from 'react';
import { MessageSquarePlus, X, Loader2 } from 'lucide-react';
import { submitFeedbackAPI, trackAnalyticsEventAPI } from '../utils/api';

const CATEGORIES = [
  { value: 'inaccuracy', label: 'Inaccuracy', placeholder: 'Which result was incorrect and what\'s the right answer?' },
  { value: 'suggestion', label: 'Suggestion', placeholder: 'What feature or change would you like to see?' },
  { value: 'question', label: 'Question', placeholder: 'What would you like to know?' },
  { value: 'other', label: 'Other', placeholder: 'What should we know?' },
];

// Ingredient-level corrections sent by the user are queued for admin review
// via /api/feedback. Status values must match the backend's VALID_STATUSES
// (see backend/src/routes/feedback.js).
const CORRECTION_STATUSES = [
  { value: 'vegan', label: 'Vegan' },
  { value: 'not_vegan', label: 'Not vegan' },
  { value: 'ambiguous', label: 'It depends (source-specific)' },
];
const MAX_CORRECTION_INGREDIENT = 100;

const CHAR_WARNING_THRESHOLD = 7000;
const MAX_CHARS = 8000;
const AUTO_CLOSE_DELAY = 2500;

export function FeedbackWidget ({ context }) {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('inaccuracy');
  const [message, setMessage] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [correctionIngredient, setCorrectionIngredient] = useState('');
  const [correctionStatus, setCorrectionStatus] = useState('');
  const autoCloseTimer = useRef(null);

  const flaggedIngredients = Array.isArray(context?.flaggedIngredients)
    ? context.flaggedIngredients
    : [];

  const resetForm = useCallback(() => {
    setCategory('inaccuracy');
    setMessage('');
    setContactEmail('');
    setStatus(null);
    setCorrectionIngredient('');
    setCorrectionStatus('');
  }, []);

  const handleClose = useCallback(() => {
    if (autoCloseTimer.current) clearTimeout(autoCloseTimer.current);
    setOpen(false);
    resetForm();
  }, [resetForm]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, handleClose]);

  useEffect(() => {
    return () => {
      if (autoCloseTimer.current) clearTimeout(autoCloseTimer.current);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);
    const trimmed = message.trim();
    if (!trimmed) {
      setStatus({ type: 'error', text: 'Please enter a message.' });
      return;
    }

    // Build the optional ingredient correction. Both fields must be set;
    // partial corrections are dropped because the backend rejects them anyway.
    let correction;
    if (category === 'inaccuracy') {
      const ing = correctionIngredient.trim();
      if (ing && correctionStatus) {
        if (ing.length > MAX_CORRECTION_INGREDIENT) {
          setStatus({
            type: 'error',
            text: `Ingredient name must be ${MAX_CORRECTION_INGREDIENT} characters or fewer.`,
          });
          return;
        }
        correction = {
          ingredient: ing,
          status: correctionStatus,
          note: trimmed.slice(0, 500),
        };
      } else if (ing && !correctionStatus) {
        setStatus({ type: 'error', text: 'Pick what the correct verdict should be for that ingredient.' });
        return;
      } else if (!ing && correctionStatus) {
        setStatus({ type: 'error', text: 'Type or pick the ingredient you\'re correcting.' });
        return;
      }
    }

    setSubmitting(true);
    try {
      const result = await submitFeedbackAPI({
        category,
        message: trimmed,
        contactEmail: contactEmail.trim() || undefined,
        context: context || undefined,
        correction,
      });
      trackAnalyticsEventAPI({
        eventName: 'feedback_submit',
        feature: 'feedback',
        metadata: {
          hasCorrection: Boolean(correction),
          hasContact: Boolean(contactEmail.trim()),
        },
      });

      // When the backend confirms the correction was applied, show a
      // slightly richer success message so the user sees their input landed.
      let successText = 'Thanks — we received your feedback.';
      if (result?.correction?.applied) {
        const verb = result.correction.from ? 'updated' : 'added';
        successText = `Thanks — we ${verb} "${result.correction.ingredient}" to ${result.correction.to.replace('_', ' ')}.`;
      }
      setStatus({ type: 'success', text: successText });
      setMessage('');
      setContactEmail('');
      setCorrectionIngredient('');
      setCorrectionStatus('');
      autoCloseTimer.current = setTimeout(handleClose, AUTO_CLOSE_DELAY);
    } catch (err) {
      setStatus({
        type: 'error',
        text: err?.message || 'Something went wrong. Please try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const currentPlaceholder = CATEGORIES.find((c) => c.value === category)?.placeholder || 'What should we know?';

  return (
    <>
      <button
        type="button"
        onClick={() => {
          trackAnalyticsEventAPI({ eventName: 'feedback_open', feature: 'feedback' });
          setOpen(true);
        }}
        className="fixed bottom-24 right-4 z-[70] flex items-center gap-2 rounded-full bg-green-600 px-4 py-3 text-white shadow-lg transition-colors hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 sm:right-6"
        aria-label="Send feedback"
        title="Send feedback"
      >
        <MessageSquarePlus className="w-5 h-5 shrink-0" aria-hidden />
        <span className="text-sm font-medium hidden sm:inline">Feedback</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-4 bg-black/40"
          role="dialog"
          aria-modal="true"
          aria-labelledby="feedback-dialog-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) handleClose();
          }}
        >
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-3 p-5 border-b border-gray-100">
              <div>
                <h2 id="feedback-dialog-title" className="text-lg font-semibold text-gray-900">
                  Send feedback
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Inaccuracies, suggestions, or questions — we read everything.
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  Feedback is saved for admin review. Email is optional and only needed if you want a reply.
                </p>
              </div>
              <button
                type="button"
                onClick={handleClose}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <label htmlFor="feedback-category" className="block text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                <select
                  id="feedback-category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="feedback-message" className="block text-sm font-medium text-gray-700 mb-1">
                  Message
                </label>
                <textarea
                  id="feedback-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={5}
                  maxLength={MAX_CHARS}
                  placeholder={currentPlaceholder}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-y min-h-[120px]"
                />
                {message.length >= CHAR_WARNING_THRESHOLD && (
                  <p className={`text-xs mt-1 ${message.length >= MAX_CHARS ? 'text-red-500 font-medium' : 'text-gray-500'}`}>
                    {message.length} / {MAX_CHARS}
                  </p>
                )}
              </div>

              {category === 'inaccuracy' && (
                <fieldset className="border border-gray-200 rounded-lg p-3 space-y-3">
                  <legend className="px-1 text-sm font-medium text-gray-700">
                    Fix an ingredient (optional)
                  </legend>
                  <p className="text-xs text-gray-500 -mt-1">
                    Tell us if a specific ingredient was misclassified — we&apos;ll review it before changing VegAI&apos;s rules.
                  </p>

                  {flaggedIngredients.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {flaggedIngredients.map((ing) => {
                        const selected = correctionIngredient.trim().toLowerCase() === ing.toLowerCase();
                        return (
                          <button
                            type="button"
                            key={ing}
                            onClick={() => setCorrectionIngredient(ing)}
                            className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                              selected
                                ? 'bg-green-600 text-white border-green-600'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                            aria-pressed={selected}
                          >
                            {ing}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  <div>
                    <label htmlFor="correction-ingredient" className="block text-xs font-medium text-gray-600 mb-1">
                      Ingredient
                    </label>
                    <input
                      id="correction-ingredient"
                      type="text"
                      value={correctionIngredient}
                      onChange={(e) => setCorrectionIngredient(e.target.value)}
                      maxLength={MAX_CORRECTION_INGREDIENT}
                      placeholder="e.g. carnauba wax"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>

                  <div>
                    <span className="block text-xs font-medium text-gray-600 mb-1">
                      Should be
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {CORRECTION_STATUSES.map((s) => {
                        const selected = correctionStatus === s.value;
                        return (
                          <button
                            type="button"
                            key={s.value}
                            onClick={() =>
                              setCorrectionStatus((prev) => (prev === s.value ? '' : s.value))
                            }
                            className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                              selected
                                ? 'bg-green-600 text-white border-green-600'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                            aria-pressed={selected}
                          >
                            {s.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </fieldset>
              )}

              <div>
                <label htmlFor="feedback-email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email (optional)
                </label>
                <input
                  id="feedback-email"
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="If you want a reply"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  autoComplete="email"
                />
              </div>

              {status && (
                <div
                  role="status"
                  className={`text-sm rounded-lg px-3 py-2 ${
                    status.type === 'success'
                      ? 'bg-green-50 text-green-800'
                      : 'bg-red-50 text-red-800'
                  }`}
                >
                  {status.text}
                </div>
              )}

              <div className="flex flex-wrap gap-2 justify-end pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 text-white font-medium hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting && <Loader2 className="w-4 h-4 animate-spin" aria-hidden />}
                  Send
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
